import { useEffect, useState, type FormEvent } from 'react';
import { Download, Save, Settings2 } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { AdminSettings } from '../types';
import { centsToInput, currencyInputToCents } from '../utils';
import { Button, EmptyBlock, Field, PageHeader, Panel, Textarea, TextInput } from '../components/ui';

export function SettingsPage() {
  const { settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteMeta, setInviteMeta] = useState<AdminSettings | null>(null);

  useEffect(() => setForm(settings), [settings]);
  useEffect(() => {
    if (!permissions.canEditSettings) return;
    void api<{ settings: AdminSettings }>('/api/admin/settings').then(data => setInviteMeta(data.settings)).catch(() => setInviteMeta(null));
  }, [permissions.canEditSettings]);

  if (!permissions.canEditSettings) return <EmptyBlock title="无权访问" description="系统设置仅对管理员开放。" />;

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm(current => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          siteName: form.siteName,
          currency: form.currency,
          initBalanceCents: currencyInputToCents(String(form.initBalanceCents / 100)),
          registrationEnabled: form.registrationEnabled,
          registrationInviteCode: inviteCode || undefined,
          registrationNotice: form.registrationNotice,
          commissionPolicy: form.commissionPolicy,
          marketPolicy: form.marketPolicy,
          auctionPolicy: form.auctionPolicy
        })
      });
      setInviteCode('');
      const latest = await api<{ settings: AdminSettings }>('/api/admin/settings');
      setInviteMeta(latest.settings);
      await refresh();
      toast('系统设置已保存', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '保存失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="系统设置" description="管理站点名称、货币、注册开关和业务说明。" />
      <div className="two-column wide-left">
        <Panel title="基础设置" eyebrow="SYSTEM">
          <form className="stack-form" onSubmit={submit}>
            <div className="form-grid">
              <Field label="站点名称"><TextInput value={form.siteName} onChange={event => update('siteName', event.target.value)} maxLength={40} required /></Field>
              <Field label="虚拟货币名称"><TextInput value={form.currency} onChange={event => update('currency', event.target.value)} maxLength={12} required /></Field>
            </div>
            <Field label="新成员初始资金"><TextInput type="number" min="0" step="0.01" value={centsToInput(form.initBalanceCents)} onChange={event => update('initBalanceCents', currencyInputToCents(event.target.value))} /></Field>
            <label className="check-line"><input type="checkbox" checked={form.registrationEnabled} onChange={event => update('registrationEnabled', event.target.checked)} /> 允许新成员自助注册</label>
            <Field label="更换注册邀请码" hint="仅在需要轮换时填写；邀请码只保存哈希，保存后无法找回原文">
              <TextInput value={inviteCode} onChange={event => setInviteCode(event.target.value.toUpperCase())} minLength={8} maxLength={64} pattern="[A-Za-z0-9_-]+" placeholder="输入新的邀请码" autoComplete="off" />
            </Field>
            <p className="form-note">当前邀请码状态：{inviteMeta?.registrationInviteConfigured ? '已设置，前缀 ' + (inviteMeta.registrationInvitePrefix || '****') + '****' : '未设置'}</p>
            <Field label="注册说明"><Textarea value={form.registrationNotice} onChange={event => update('registrationNotice', event.target.value)} rows={2} maxLength={500} /></Field>
            <Field label="委托规则说明"><Textarea value={form.commissionPolicy} onChange={event => update('commissionPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Field label="市场规则说明"><Textarea value={form.marketPolicy} onChange={event => update('marketPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Field label="拍卖规则说明"><Textarea value={form.auctionPolicy} onChange={event => update('auctionPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Button variant="primary" type="submit" loading={busy}><Save size={17} /> 保存设置</Button>
          </form>
        </Panel>
        <div className="side-stack">
          <Panel title="权限边界" eyebrow="SECURITY">
            <div className="policy-list">
              <p><Settings2 size={17} /><span><strong>成员</strong>只能委托、购买、拍卖和兑换。</span></p>
              <p><Settings2 size={17} /><span><strong>管理员</strong>可管理普通成员、公告、兑换码和系统设置。</span></p>
              <p><Settings2 size={17} /><span><strong>超级管理员</strong>可管理管理员、任命与撤销管理员。</span></p>
            </div>
          </Panel>
          {permissions.canExportBackup && (
            <Panel title="完整备份" eyebrow="BACKUP">
              <p className="muted">导出包含全部业务表和审计记录。文件含敏感账号数据，请妥善保管。</p>
              <Button variant="danger" className="wide" onClick={() => void (async () => {
                const approved = await confirm({ title: '确认导出完整备份？', body: '导出文件包含所有账号数据、业务数据和审计记录，请妥善保管。', danger: true, confirmText: '确认导出' });
                if (approved) window.location.href = '/api/admin/backup';
              })()}><Download size={17} /> 导出 JSON 备份</Button>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
